import { useEffect, useState } from 'react';
import { Address } from 'viem';

import { fontStyles } from '@/styles/typography';

import { CheckCircleIcon } from '@/ui/Icons/CheckCircleIcon';
import { CopyIcon } from '@/ui/Icons/CopyIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { formatShortAddress } from '../../../utils/address';

const TWO_SECONDS = 2000;

export const CopyAddress = ({
  address,
  side = 'top',
}: {
  address: Address | string;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) => {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const timeout = isCopied ? setTimeout(() => setIsCopied(false), TWO_SECONDS) : undefined;
    return () => clearTimeout(timeout);
  }, [isCopied]);

  const onCopyAddress = () => {
    navigator.clipboard.writeText(address);
    setIsCopied(true);
  };

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <button
          className={fontStyles({
            variant: 'BODY_MD',
            class: [
              'inline-flex',
              'items-center',
              'hover:bg-token-gray-200',
              'active:bg-token-gray-300',
              'focus-visible:outline-token-gray-500',
              'focus-visible:outline-[3px]',
              'cursor-pointer',
              '-mr-3',
              'px-3',
              'h-9',
              'w-fit',
              'rounded-sm',
            ],
          })}
          onClick={onCopyAddress}
          aria-label="Copy address"
        >
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['items-center', 'flex', 'pointer-events-none'],
            })}
          >
            {formatShortAddress(address, 5, 3)}
            &nbsp;
            {isCopied ? (
              <CheckCircleIcon className="text-token-gray-800 w-4 h-4 hover:text-token-gray-200 disabled:opacity-50" />
            ) : (
              <CopyIcon className="text-token-gray-800 w-4 h-4 hover:text-token-gray-200 disabled:opacity-50" />
            )}
          </span>
        </button>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>{isCopied ? 'Copied' : 'Copy address'}</MatchaTooltipContent>
      <MatchaTooltipPopover side={side} sideOffset={7} />
    </MatchaTooltip>
  );
};
